import { Component, OnInit, ViewChild } from '@angular/core';
import { GoogleMap, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpService } from 'app/services/http.service';
import { autoFitBoundaries } from 'app/google-map-utils';
import { AssetStatuses } from 'app/models/control-center/asset.model';
import { Utils } from 'app/utils';
import { environment } from 'environments/environment';
import { PermissionType } from 'app/models/control-center/permission.model';
import { TableActionPermissions } from 'app/components/assetTable/assetTable.component';

@Component({
    selector: 'app-assets',
    templateUrl: './assets.component.html',
    styleUrls: ['./assets.component.scss'],
})
export class AssetsComponent implements OnInit {

    loading = true;
    dataSource: any[] = [];

    @ViewChild(GoogleMap) map?: GoogleMap;
    @ViewChild(MapInfoWindow) infoWindow?: MapInfoWindow;

    displayedColumns: any = [
        { key: 'status', label: 'Status', type: 'badge', width: '140px' },
        { key: 'statusDetail', label: 'Status Details', type: 'string', width: '170px' },
        { key: 'company', label: 'Company', type: 'string' },
        { key: 'name', label: 'Name', type: 'string', width: '140px' },
        { key: 'assetModel', label: 'Model', type: 'string', width: '120px' },
        { key: 'serialNumber', label: 'Serial Number', type: 'string', width: '140px' },
        { key: 'version', label: 'Version', type: 'string', width: '100px' },
        { key: 'locationUpdated', label: 'Last Seen', type: 'date', width: '170px' },
        { key: 'menu', label: 'Actions', type: 'menu', width: '60px', sortable: false },
    ];
    tableFilter: any = [];

    permissions;
    actionPermissions: TableActionPermissions = {
        'export': PermissionType.Read,
    };
    assetTypeId = '';
    assetType = '';

    warehouses = [];
    models = [];
    people = [];
    sites = [];
    warehouseId: number;
    expectedDeliveryDate: string;
    modelId: number;
    file: File;
    waltAssetType = environment.waltAssetType.toString();
    tableActions = [{ name: 'Update Status', key: 'updateStatus', requireItemSelection: true }];
    bulkUpdateStatuses: {
        status?: AssetStatuses;
        assetIds?: number[];
        warehouseId?: number;
        warehouseLocation?: string;
        personId?: number;
        siteId?: number;
        numAssets?: number;
    } = {};
    assetStatuses = AssetStatuses;

    formatDate = Utils.formatDate;
    importResult = '';
    bulkActionResults = [];

    @ViewChild('importDialog') importDialog: any;
    @ViewChild('importResultDialog') importResultDialog: any;
    @ViewChild('updateStatusDialog') updateStatusDialog: any;
    @ViewChild('bulkActionResultsDialog') bulkActionResultsDialog: any;

    constructor(private readonly _httpService: HttpService, private readonly _router: Router, private readonly route: ActivatedRoute, private readonly dialog: MatDialog) { }

    formatPersonDisplay = (person: any) => Utils.formatPersonDisplay(person);

    async ngOnInit(): Promise<void> {
        HttpService.clearCache('inventory');
        this.permissions = await this._httpService.get('people/authenticated/permissions');
        this.load();
    }

    paginationItemManipulation(asset) {
        asset.name = asset.alais ? asset.alais : asset.serialNumber;
        asset.expectedDeliveryDate = asset.expectedDeliveryDate ? new Date(asset.expectedDeliveryDate).toISOString().split('T')[0] : null;

        switch (asset.status) {
            case 'Maintenance':
                asset.color = '#FFA500';
                asset.statusDetail = asset.personName;
                break;
            case 'Checked Out':
                asset.color = '#b53737';
                asset.statusDetail = asset.personName;
                break;
            case 'Available':
                asset.color = '#62bd69';
                asset.statusDetail = asset.warehouse + ' - ' + asset.warehouseLocation;
                break;
            case 'Provisioning':
                asset.color = '#aaaaaa';
                asset.statusDetail = asset.warehouse;
                break;
            case 'Incoming Forecast':
                asset.color = '#aaaaaa';
                asset.statusDetail = asset.expectedDeliveryDate;
                break;
            default:
                asset.color = '#aaaaaa';
        }
        return asset;
    }

    async load() {
        const loadPeople = async () => {
            // We only use people for bulk updates, no need to await this to display the page
            this.people = await this._httpService.get('people');
        };

        this.loading = true;
        this.assetTypeId = this.route.snapshot.params['id'];
        loadPeople();

        await Promise.all([
            (async () => this.assetType = (await this._httpService.get('inventory/assetTypes/' + this.assetTypeId)).name)(),
            (async () => this.permissions = await this._httpService.get('people/authenticated/permissions'))(),
            (async () => this.warehouses = await this._httpService.get('inventory/warehouses'))(),
            (async () => this.models = await this._httpService.get(`inventory/assetTypes/${this.assetTypeId}/assetModels`))(),
            (async () => this.sites = await this._httpService.get('companies/sites'))(),
        ]);

        this.tableFilter = [
            {
                show: true, type: 'select', name: 'Status', key: 'status', options: Object.keys(AssetStatuses).map(k => {
                    return { value: k, label: AssetStatuses[k] };
                }),
            },
            { show: true, type: 'select', name: 'Status Details', key: 'statusDetail', options: [], populateValues: true },
            { show: true, type: 'select', name: 'Company', key: 'company', options: [], populateValues: true },
            { show: true, type: 'input', name: 'Name', key: 'name', options: {} },
            { show: true, type: 'select', name: 'Model', key: 'assetModel', options: [], populateValues: true },
            { show: true, type: 'input', name: 'Serial Number', key: 'serialNumber', options: {} },
            { show: true, type: 'input', name: 'Version', key: 'version', options: {} },
            { show: true, type: 'dateRange', name: 'Last Seen', key: 'lastSeen', options: {} },
        ];

        this.loading = false;
    }

    add() {
        this._router.navigateByUrl('/inventory/assets/0?assetTypeId=' + this.assetTypeId);
    }

    rowClick(id: any) {
        this._router.navigateByUrl('/inventory/assets/' + id);
    }

    async delete(ids: []) {
        this.loading = true;
        const promises = [];

        for (let i = 0; i < ids.length; i++) {
            promises.push(this._httpService.delete(`inventory/assets/${ids[i]}`));
        }
        const results = await Promise.allSettled(promises);

        const errors = results
            .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
            .map(r => ({
                status: 'rejected',
                message: r.reason?.error?.message,
            }));

        if (errors && errors.length > 0) {
            this.bulkActionResults = errors.map(e => e.message);
            this.dialog.open(this.bulkActionResultsDialog);
        }

        await this.load();
    }

    showResultDialog() {
        this.dialog.open(this.importResultDialog);
    }

    showImport() {
        const dialogRef = this.dialog.open(this.importDialog);
        dialogRef.afterClosed().subscribe(async result => {
            if (result !== undefined) {
                if (result === 'import') {
                    try {
                        const formData = new FormData();

                        formData.append('file', this.file);
                        formData.append('assetModelId', this.modelId.toString());
                        formData.append('assetTypeId', this.assetTypeId.toString());
                        formData.append('expectedDeliveryDate', this.expectedDeliveryDate.toString());
                        formData.append('warehouseId', this.warehouseId.toString());

                        const result = await this._httpService.postForm('inventory/assets/import', formData);
                        if (result.status === 'error') {
                            this.importResult = `An error occurred while importing assets: ${result.error}`;
                            return;
                        }
                        this.importResult = 'Assets imported successfully.';
                    } catch (error) {
                        console.error(error);
                        this.importResult = `An error occurred while importing assets`;
                    } finally {
                        this.showResultDialog();
                        this.file = null;
                        this.modelId = null;
                        this.assetTypeId = null;
                        this.expectedDeliveryDate = null;
                        this.warehouseId = null;
                        await this.load();
                    }
                }
            }
        });
    }

    async onFileSelected(event) {
        this.file = event.target.files[0];
    }

    toNum = Number;

    handleTableAction(action: { key: string, ids: number[] }): void {
        this.bulkUpdateStatuses = {};
        this.bulkUpdateStatuses.assetIds = action.ids;
        const dialogRef = this.dialog.open(this.updateStatusDialog);
        dialogRef.afterClosed().subscribe(async result => {
            if (result !== undefined) {
                if (result === 'update') {
                    this.loading = true;
                    const result = await this._httpService.put('inventory/assets/statuses', this.bulkUpdateStatuses);

                    if (result.errors && result.errors.length > 0) {
                        this.bulkActionResults = result.errors.map(e => e.reason.message);
                        this.dialog.open(this.bulkActionResultsDialog);
                    }

                    await this.load();
                    this.loading = false;
                }
            }
        });
    }

    openedAsset?: any;

    markerClick(asset: any, marker: MapMarker) {
        if (this.infoWindow) {
            this.openedAsset = asset;
            this.infoWindow.open(marker);
        }
    }

    private didFitBounds = false;

    fitBounds() {
        if (this.didFitBounds) {
            return;
        }
        const positions = this.dataSource
            ?.filter(asset => asset.longitude && asset.latitude)
            .map(asset => ({ lat: +asset.longitude, lng: +asset.latitude }));
        this.didFitBounds = autoFitBoundaries(this.map, positions);
    }

    exportRowOverride(row: any) {
        if (row['eIdOne']) row['eIdOne'] = `'${row['eIdOne']}'`;
        if (row['eIdTwo']) row['eIdTwo'] = `'${row['eIdTwo']}'`;
        if (row['imeiOne']) row['imeiOne'] = `'${row['imeiOne']}'`;
        if (row['imeiTwo']) row['imeiTwo'] = `'${row['imeiTwo']}'`;
        if (row['dateAssignedToSite']) row['dateAssignedToSite'] = Utils.formatDate(row['dateAssignedToSite']);
    }

}
